"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { useEffect, useRef, useState } from "react";
import Mark from "./Mark";
import { site } from "./site";

const SiteHeader = () => {
  const pathname = usePathname();
  const { status } = useSession();
  const [open, setOpen] = useState(false);
  const [drop, setDrop] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setOpen(false);
    setDrop(false);
  }, [pathname]);
  useEffect(() => {
    if (!drop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrop(false);
        menuRef.current?.querySelector("button")?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setDrop(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [drop]);
  const menu = site.menu;

  const current = (href: string) => (pathname === href || pathname.startsWith(href + "/") ? "page" : undefined);
  const signedIn = status === "authenticated";
  const links = [...site.nav, ...(signedIn ? [{ href: "/ai-tools", label: "Tools" }] : [])];
  const account = signedIn ? (
    <button type="button" className="nav-link" onClick={() => signOut({ callbackUrl: "/" })}>
      Sign out
    </button>
  ) : (
    <Link href="/auth/signin" className="nav-link">
      Sign in
    </Link>
  );

  const ContactLink = ({ className, onClick }: { className?: string; onClick?: () => void }) =>
    site.contact.href.startsWith("/") ? (
      <Link href={site.contact.href} className={className} onClick={onClick}>
        {site.contact.label}
      </Link>
    ) : (
      <a href={site.contact.href} className={className} onClick={onClick}>
        {site.contact.label}
      </a>
    );

  return (
    <header className="site-header" data-open={open || undefined}>
      <div className="wrap header-bar">
        <Link href="/" className="brand" aria-label={`${site.name} home`}>
          <Mark />
          {site.name}
        </Link>
        <nav className="site-nav" aria-label="Main">
          {menu && (
            <div className="has-menu" ref={menuRef}>
              <button
                type="button"
                className="nav-link menu-trigger"
                aria-expanded={drop}
                aria-controls="platform-menu"
                aria-current={current(menu.href)}
                onClick={() => setDrop(!drop)}
              >
                {menu.label}
              </button>
              <div id="platform-menu" className="nav-menu" hidden={!drop}>
                {menu.items.map((i) => (
                  <Link key={i.href} href={i.href}>
                    <strong>{i.label}</strong>
                    <span>{i.text}</span>
                  </Link>
                ))}
                <Link href={menu.href} className="nav-menu-all">
                  Overview
                </Link>
              </div>
            </div>
          )}
          {links.map((l) => (
            <Link key={l.href} href={l.href} className="nav-link" aria-current={current(l.href)}>
              {l.label}
            </Link>
          ))}
          {account}
          <ContactLink className="btn-sm" />
          <button
            type="button"
            className="pill-outline menu-button"
            aria-expanded={open}
            aria-controls="site-menu"
            onClick={() => setOpen(!open)}
          >
            {open ? "Close" : "Menu"}
          </button>
        </nav>
      </div>
      <div id="site-menu" className="wrap menu-panel">
        {menu && (
          <>
            <Link href={menu.href} aria-current={pathname === menu.href ? "page" : undefined} onClick={() => setOpen(false)}>
              {menu.label} overview
            </Link>
            {menu.items.map((i) => (
              <Link key={i.href} href={i.href} className="menu-sub" aria-current={current(i.href)} onClick={() => setOpen(false)}>
                {i.label}
              </Link>
            ))}
          </>
        )}
        {links.map((l) => (
          <Link key={l.href} href={l.href} aria-current={current(l.href)} onClick={() => setOpen(false)}>
            {l.label}
          </Link>
        ))}
        <ContactLink onClick={() => setOpen(false)} />
        {signedIn ? (
          <button type="button" onClick={() => signOut({ callbackUrl: "/" })}>
            Sign out
          </button>
        ) : (
          <Link href="/auth/signin" onClick={() => setOpen(false)}>
            Sign in
          </Link>
        )}
      </div>
    </header>
  );
};

export default SiteHeader;
